// CSV serialization for Manufacturing → Part uses export (round-trips the
// bulk-import column contract in materialUsesBulkImportExample.ts).

import type { MfgMaterialUse, MfgMaterialUseMember } from "@/lib/manufacturing/types";

export const MATERIAL_USE_EXPORT_HEADERS = [
  "use_code",
  "use_name",
  "unit",
  "notes",
  "is_active",
  "resolve_mode",
  "finish_channel",
  "member_material_code",
  "member_qty_per_use",
  "preference_rank",
  "member_is_active",
  "is_primary",
  "finish_code",
] as const;

const NEEDS_QUOTING = /[",\n\r]/;

function escapeCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  const s = typeof value === "string" ? value : String(value);
  if (!NEEDS_QUOTING.test(s)) return s;
  return `"${s.replace(/"/g, '""')}"`;
}

export type MaterialUseExportDetail = {
  use: Pick<
    MfgMaterialUse,
    "use_code" | "name" | "unit" | "notes" | "is_active" | "resolve_mode" | "finish_channel"
  >;
  members: ReadonlyArray<
    Pick<
      MfgMaterialUseMember,
      | "material_code"
      | "qty_per_use"
      | "preference_rank"
      | "is_active"
      | "is_primary"
      | "finish_code"
    >
  >;
};

function rowsToCsv(headers: readonly string[], rows: Array<Array<unknown>>): string {
  const lines = [headers.join(",")];
  for (const row of rows) {
    lines.push(row.map(escapeCell).join(","));
  }
  return lines.join("\n") + "\n";
}

/** Flatten use + members into CSV (header-only use emits one empty member row). */
export function materialUsesToCsv(details: ReadonlyArray<MaterialUseExportDetail>): string {
  const rows: Array<Array<unknown>> = [];
  for (const d of details) {
    const header = [
      d.use.use_code,
      d.use.name,
      d.use.unit,
      d.use.notes ?? "",
      d.use.is_active ? "true" : "false",
      d.use.resolve_mode,
      d.use.finish_channel ?? "",
    ];
    if (d.members.length === 0) {
      rows.push([...header, "", "", "", "", "", ""]);
      continue;
    }
    for (const m of d.members) {
      rows.push([
        ...header,
        m.material_code,
        m.qty_per_use,
        m.preference_rank,
        m.is_active ? "true" : "false",
        m.is_primary ? "true" : "false",
        m.finish_code ?? "",
      ]);
    }
  }
  return rowsToCsv(MATERIAL_USE_EXPORT_HEADERS, rows);
}
